/* =========================================================================
   Place des Arts — pagination « Voir plus » du catalogue d'œuvres
   (collection + recherche). Rendu : snippets/pda-load-more.liquid.
   -------------------------------------------------------------------------
   Aucun chargement au scroll : le clic « Voir N de + » appelle
   resultsList.loadNextPage() (méthode publique PDA de paginated-list.js),
   c.-à-d. exactement le fetch/append natif que le sentinel d'infinite
   scroll déclenchait. (« Voir tous » supprimé 2026-07-07 — rafale de requêtes.)
   borné par data-last-page (garde anti-boucle infinie) + timeout réseau.

   Resynchro : un MutationObserver sur <results-list> recalcule les
   compteurs après chaque append ET après chaque re-rendu de facets.js
   (filtres) — les valeurs sont toujours relues depuis le DOM courant.
   ========================================================================= */

/* ⛔ MÊMES CLÉS QUE LE SSR (`snippets/pda-load-more.liquid`), qui peint la MÊME phrase
 * avant que ce module ne la reprenne. Tant que le SSR portait une clé et le JS des
 * littéraux, la phrase changeait de langue au premier repaint.
 * `trad` et non `t` : cohérence avec les autres modules PDA. Il DÉGRADE en peignant la
 * clé plutôt que de tuer le module — donc la pagination. */
const trad = (cle, params) =>
  (window.PDA && typeof window.PDA.t === 'function') ? window.PDA.t(cle, params) : cle;

const FETCH_TIMEOUT_MS = 15000;

class PdaLoadMore extends HTMLElement {
  /** @type {boolean} */
  #busy = false;

  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {number | undefined} */
  #rafId;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);

    const list = this.closest('results-list');
    if (list) {
      this.#observer = new MutationObserver(() => this.#scheduleUpdate());
      this.#observer.observe(list, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-last-page', 'data-total'],
      });
    }

    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  /**
   * État courant, relu depuis le DOM (robuste aux re-rendus de facets.js).
   * @returns {{ total: number, pageSize: number, lastPage: number, maxPage: number, shown: number, remaining: number }}
   */
  #counts() {
    const grid = this.closest('results-list')?.querySelector('[ref="grid"]');
    const total = Number(this.dataset.total) || 0;
    const pageSize = Number(this.dataset.pageSize) || 48;
    const lastPage = Number(grid?.dataset.lastPage) || 1;

    let maxPage = 1;
    if (grid) {
      for (const card of grid.querySelectorAll(':scope > [ref="cards[]"]')) {
        const page = Number(/** @type {HTMLElement} */ (card).dataset.page) || 1;
        if (page > maxPage) maxPage = page;
      }
    }

    const shown = Math.min(maxPage * pageSize, total);
    return { total, pageSize, lastPage, maxPage, shown, remaining: Math.max(total - shown, 0) };
  }

  #scheduleUpdate() {
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = undefined;
      this.#update();
    });
  }

  #update() {
    const { total, pageSize, shown, remaining, maxPage, lastPage } = this.#counts();
    /* Chargé en `type="module"` (snippets/pda-load-more.liquid), donc après le runtime
     * posé en <head> ; le repli de `trad` ne couvre que l'échec de chargement de
     * `pda-i18n.js`, pas un problème d'ordre. */

    // F13 : le lien « Voir plus » suit la vraie page suivante (utile au
    // clic-molette/nouvel onglet ; les crawlers lisent le href SSR initial).
    const nextA = this.querySelector('a[data-pda-load-more]');
    if (nextA) {
      try {
        const u = new URL(window.location.href);
        u.searchParams.set('page', String(Math.min(maxPage + 1, lastPage)));
        nextA.setAttribute('href', u.pathname + u.search);
      } catch {}
    }

    /* ⛔ LA PHRASE ENTIÈRE, PAS DEUX NOMBRES DANS DEUX <span>. Le SSR rend LUI AUSSI la
     * phrase entière, déjà formatée — ce n'est pas un hook vide, et c'est justement ce
     * qui supprime le clignotement : les deux écrivent la même chaîne. La seule chose
     * qui compte ici est donc la PARITÉ SSR↔JS.
     * `PDA.t` reçoit les nombres BRUTS et fait lui-même le format de la
     * locale ET le choix de la forme plurielle — un `format()` appliqué avant l'appel
     * produirait une chaîne, et le runtime perdrait le nombre dont il a besoin. */
    this.#setText('[data-pda-pager-count]', trad('pda.catalogue.pager.vu', { count: shown, vus: shown, total }));
    this.#setText('[data-pda-next-label]', trad('pda.catalogue.pager.suite', { count: Math.min(pageSize, remaining) }));

    /** @type {HTMLElement | null} */
    const fill = this.querySelector('[data-pda-progress]');
    if (fill) {
      const pct = total > 0 ? Math.round((shown / total) * 100) : 100;
      fill.style.width = `${pct}%`;
    }

    /** @type {HTMLElement | null} */
    const actions = this.querySelector('[data-pda-actions]');
    if (actions) actions.hidden = remaining <= 0;
  }

  /**
   * @param {string} selector
   * @param {string} value
   */
  #setText(selector, value) {
    const element = this.querySelector(selector);
    if (element && element.textContent !== value) element.textContent = value;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest('[data-pda-load-more]')) return;
    // F13 : le déclencheur est un vrai lien ?page=N (crawl + no-JS) — en JS on
    // intercepte et on garde le fetch/append natif.
    event.preventDefault();
    if (this.#busy) return;

    this.#loadPages(1);
  };

  /**
   * Charge `count` pages séquentiellement via la mécanique native.
   * @param {number} count - nombre de pages à charger (1 = « Voir plus »).
   */
  async #loadPages(count) {
    this.#busy = true;
    this.toggleAttribute('data-loading', true);
    this.#setButtonsDisabled(true);

    try {
      await customElements.whenDefined('results-list');
      const list = this.closest('results-list');
      // @ts-ignore — loadNextPage est ajoutée par paginated-list.js (PDA)
      if (!list || typeof list.loadNextPage !== 'function') return;

      // Garde anti-boucle : jamais plus d'itérations que de pages restantes.
      const { maxPage, lastPage } = this.#counts();
      let guard = Math.min(count, Math.max(lastPage - maxPage, 0));

      while (guard > 0) {
        guard -= 1;
        // @ts-ignore
        const appended = await this.#withTimeout(list.loadNextPage());
        if (!appended) break;
        this.#update();
      }
    } finally {
      this.#busy = false;
      this.removeAttribute('data-loading');
      this.#setButtonsDisabled(false);
      this.#update();
    }
  }

  /** @param {boolean} disabled */
  #setButtonsDisabled(disabled) {
    for (const el of this.querySelectorAll('button, a[data-pda-load-more]')) {
      if (el instanceof HTMLButtonElement) el.disabled = disabled;
      else {
        // F13 : le déclencheur peut être un <a> — pas de .disabled natif.
        el.toggleAttribute('data-disabled', disabled);
        el.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }
    }
  }

  /**
   * Évite un blocage définitif des boutons si un fetch ne résout jamais.
   * @param {Promise<boolean>} promise
   * @returns {Promise<boolean>}
   */
  #withTimeout(promise) {
    return Promise.race([
      promise,
      new Promise((resolve) => {
        setTimeout(() => resolve(false), FETCH_TIMEOUT_MS);
      }),
    ]);
  }
}

if (!customElements.get('pda-load-more')) {
  customElements.define('pda-load-more', PdaLoadMore);
}
